/**
 * Storefront GA / dataLayer tracking (Homepage v2, Offers v2, Showroom v2).
 *
 * Schema contract for click_cta / offers / newsletter:
 *   content_group, page_section, location.onpage, click_text,
 *   cta_type (BM Button Color: Primary | Secondary | Light | …),
 *   link_url (always present, '' when missing), model, grade, plus section identity fields.
 */
const OFFER_CARD_FAMILIES = ['.product-tile', '.promotion-tile'];
const OFFER_FLYOUT_FAMILIES = ['.product-tile-promotions', '.promotion-tile-offer'];
const OFFER_CARD_ACTIONS = ['.action-offer-details', '.action-build-and-price'];
const OFFER_SIDESHEET_TARGETS = ['[data-view^="promotion-"]', '.modal-footer a', '.accordion-title[data-toggle="collapse"]', '.close'];
const SHOWROOM_TILE_ACTIONS = ['.action-showroom'];

const HEADING_SECTION_RULES = [
    { pattern: /meet the.*line-?up/i, page_section: 'Meet the line-up', location_onpage: 'Lineup', identity: 'card_name' },
    { pattern: /why honda/i, page_section: 'Why Honda', location_onpage: 'Why Honda', identity: 'card_title' },
    { pattern: /latest news/i, page_section: 'Latest news', location_onpage: 'Latest news', identity: 'card_title' },
    { pattern: /recall/i, page_section: 'Recalls', location_onpage: 'Recalls', identity: 'card_title' },
    { pattern: /vehicle tech/i, page_section: 'Vehicle Tech', location_onpage: 'Vehicle Tech', identity: 'component_name' }
];

/** BM Button Color enum values keyed by lowercase CSS color token. */
const BUTTON_COLOR_CTA_TYPES = {
    primary: 'Primary',
    secondary: 'Secondary',
    success: 'Success',
    danger: 'Danger',
    warning: 'Warning',
    info: 'Info',
    light: 'Light',
    dark: 'Dark',
    transparent: 'Transparent'
};

/**
 * Map a Bootstrap button class token to BM Button Color label.
 * e.g. btn-primary → Primary, btn-outline-light → Light, btn-link-secondary → Secondary.
 *
 * @param {string} className
 * @returns {string}
 */
function mapBtnClassToCtaType(className) {
    if (!className) {
        return '';
    }

    const match = className.match(/^btn-outline-([\w-]+)$/)
        || className.match(/^btn-link-([\w-]+)$/)
        || className.match(/^btn-(primary|secondary|success|danger|warning|info|light|dark|transparent)$/);

    if (!match) {
        return '';
    }

    return BUTTON_COLOR_CTA_TYPES[match[1]] || '';
}

/** BM container IDs on Homepage composites (Approach 2). */
const SECTION_ID_RULES = [
    { selector: '#why-honda', page_section: 'Why Honda', location_onpage: 'Why Honda', identity: 'card_title' },
    { selector: '#latest-news', page_section: 'Latest news', location_onpage: 'Latest news', identity: 'card_title' },
    { selector: '#split-view', page_section: 'Split view', location_onpage: 'Split view', identity: 'component_name' },
    { selector: '#recalls', page_section: 'Recalls', location_onpage: 'Recalls', identity: 'card_title' }
];

/** Headings — always preferred over body copy when naming a card. */
const TITLE_SELECTOR = '.card-title-text, .card-title, [class*="sub-title"], .h1, .h2, .h3, h1, h2, h3, .title';

/**
 * Page Designer text component, used only when a card has no heading at all. Real heading tags are
 * excluded: a section title ("Why Honda", "Latest news") renders as `h5.basic--text` and naming a
 * card after it would be wrong.
 */
const BODY_TEXT_SELECTOR = '.basic--text:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6)';

/** Card-level composites: never look past one, or a CTA would inherit its neighbour's title. */
const CARD_SELECTOR = '.product-tile, .promotion-tile, .card, .split-screen-item, .component.common--image-with-text';

/** Bootstrap grid cell — Page Designer puts one card per cell, so it bounds a title lookup too. */
const GRID_CELL_SELECTOR = '[class^="col-"], [class*=" col-"]';

/** Outermost containers a title lookup may reach. */
const TITLE_BOUNDARY_SELECTOR = CARD_SELECTOR + ', ' + GRID_CELL_SELECTOR;

/** Containers walked upwards while resolving the title of a CTA. */
const TITLE_SCOPE_SELECTOR = '.region, .basic--decorator, ' + TITLE_BOUNDARY_SELECTOR;

/**
 * @param {string[]} ancestors
 * @param {string[]} descendants
 * @returns {string}
 */
function buildDelegatedSelector(ancestors, descendants) {
    return ancestors
        .reduce((selectors, ancestor) => selectors.concat(descendants.map((descendant) => ancestor + ' ' + descendant)), [])
        .join(', ');
}

const OFFER_CARD_SELECTOR = buildDelegatedSelector(OFFER_CARD_FAMILIES, OFFER_CARD_ACTIONS);
const OFFER_SIDESHEET_SELECTOR = buildDelegatedSelector(OFFER_FLYOUT_FAMILIES, OFFER_SIDESHEET_TARGETS);
const SHOWROOM_TILE_SELECTOR = buildDelegatedSelector(OFFER_CARD_FAMILIES, SHOWROOM_TILE_ACTIONS);

const { snakeCase } = require('lodash');

const analyticsHelper = require('../utils/analyticsHelper');

/**
 * Derive the common context fields shared by every car-explorer GA event.
 *
 * @param {object} state  Directive element state
 * @returns {object}
 */
function getCarExplorerContext(state) {
    const { element } = state;
    const isConfigurator = !!element.closest('.configurator__images-slot');

    return {
        isConfigurator,
        contentType: isConfigurator ? 'configurator' : 'showroom',
        grade: isConfigurator ? (state.product?.variationAttributes?.find(a => a.id === 'grade')?.displayValue || null) : '',
        modelId: state.product?.id || null,
        model: state.product?.productName || null,
        sessionId: document.querySelector('.page')?.dataset?.sessionId || null,
        timestamp: Math.floor(Date.now() / 1000),
        view: element.getAttribute('data-view') || 'exterior'
    };
}

/**
 * Parse URL query parameters used by the car-explorer nav-arrow event.
 *
 * @returns {object}
 */
function getCarExplorerUrlParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const campaignParam = urlParams.get('camp') || '';

    return {
        suburb: urlParams.get('suburb') || null,
        postcode: urlParams.get('pc') || null,
        state: urlParams.get('state') || null,
        campaignIds: campaignParam ? decodeURIComponent(campaignParam).split('&').filter(Boolean) : null,
        subVariant: urlParams.get('sv') || null
    };
}

module.exports = {
    callToActions() {
        $('body').on('click', '.btn, .navbar-toggler, .vehicle-grades-carousel__filter-pill', (event) => {
            const target = event.currentTarget;
            const navbar = target.closest('.component.common--navbar');

            // Page Designer section CTAs (Hero, Next steps, Why Honda, …) are handled by pageSectionCta().
            if (target.classList.contains('basic--button')
                && !target.closest('.component.common--navbar, .component.common--sticky-nav, .basic--decorator.style-02')) {
                return;
            }

            // Configurator sub navigation — keeps the configuratorEvents schema, not the showroom one.
            if (navbar && navbar.classList.contains('configurator-navigation')) {
                if (!target.closest('.configurator-modal')) {
                    if (target.getAttribute('amb-action') === 'scroll_to_section') {
                        this.configuratorEvents.subnavClick(this.helpers, target.textContent.trim() || null);
                    } else if (target.classList.contains('btn-test-drive')) {
                        this.configuratorEvents.subnavCta(this.helpers, {
                            link_text: target.textContent.trim() || null,
                            cta_type: null,
                            model_id: this.helpers.configuratorProduct.model_id
                        });
                    } else if (target.classList.contains('btn-back') || target.classList.contains('btn-order-online')) {
                        this.configuratorEvents.subnavCta(this.helpers, {
                            link_text: target.textContent.trim() || null,
                            ...this.helpers.configuratorProduct
                        });
                    } else if (target.classList.contains('navbar-toggler') && target.classList.contains('expand-never')) {
                        this.configuratorEvents.subnavCta(this.helpers, {
                            link_text: 'three-dots',
                            ...this.helpers.configuratorProduct
                        });
                    }
                }
            // Showroom / Page Designer navbar
            } else if (navbar) {
                if (target.classList.contains('basic--button')) {
                    if (target.getAttribute('amb-action') === 'scroll_to_section') {
                        this.events.jumpLink(event, this.helpers);
                    } else if (target.getAttribute('href') === '#testdrive') {
                        this.events.testDrive(event, this.helpers, {
                            page_section: 'Sub navigation',
                            'location.onpage': 'Sub navigation'
                        });
                    } else if (target.href.includes('/configurator/') || target.href.includes('/Product-Show')) {
                        this.events.buildAndPrice(event, this.helpers, {
                            page_section: 'Sub navigation',
                            'location.onpage': 'Sub navigation'
                        });
                    } else if (target.getAttribute('href') === '#brochure'
                            || target.textContent.toLowerCase().includes('brochure')) {
                        this.events.viewBrochure(event, this.helpers, {
                            page_section: 'Sub navigation',
                            'location.onpage': 'Sub navigation'
                        });
                    } else if (target.querySelector('.icomoon-spec-sheet')
                            || target.textContent.toLowerCase().includes('spec sheet')) {
                        this.events.viewSpecSheet(event, this.helpers, {
                            page_section: 'Sub navigation',
                            'location.onpage': 'Sub navigation'
                        });
                    }
                } else if (target.classList.contains('navbar-toggler')) {
                    this.events.toggleNavbar(event, this.helpers);
                }
            }

            // Sticky Nav
            if (target.closest('.component.common--sticky-nav')) {
                if (target.classList.contains('basic--button') || target.classList.contains('btn')) {
                    this.events.stickyNavCta(event, this.helpers);
                }
            }

            // Vehicle Grades
            if (target.closest('.basic--decorator.style-02')) {
                const productCard = target.closest('.vehicle-grades-carousel__card') || target.closest('.basic--decorator.style-02').querySelector('.vehicle-grades-carousel__card');
                const activeColor = productCard
                    ?.querySelector('.vehicle-grades-carousel__card-swatch.active')
                    ?.closest('.vehicle-grades-carousel__card-color-switcher')
                    ?.getAttribute('title');
                const gradeMeta = {
                    page_section: 'Grades',
                    'location.onpage': 'Grades',
                    model: productCard?.dataset.modelName || null,
                    grade: productCard?.dataset.gradeDesc || null,
                    model_colour: activeColor || null
                };

                if (target.classList.contains('vehicle-grades-carousel__card-btn-build')) {
                    this.events.buildAndPrice(event, this.helpers, gradeMeta);
                } else if (target.classList.contains('vehicle-grades-carousel__card-link-features')) {
                    this.events.seeAllFeatures(event, this.helpers, gradeMeta);
                } else if (target.classList.contains('vehicle-grades-carousel__filter-mobile-btn')) {
                    this.events.toggleFilter(event, this.helpers, {
                        page_section: 'Grades',
                        'location.onpage': 'Grades',
                        model: productCard?.dataset.modelName || null
                    });
                } else if (target.classList.contains('vehicle-grades-carousel__filter-sheet-apply')) {
                    this.events.filterApply(event, this.helpers, {
                        page_section: 'Grades',
                        'location.onpage': 'Grades',
                        model: productCard?.dataset.modelName || null
                    });
                } else if (target.classList.contains('vehicle-grades-carousel__filter-pill')) {
                    this.events.filterValueSelect(event, this.helpers, {
                        page_section: 'Grades',
                        'location.onpage': 'Grades',
                        model: productCard?.dataset.modelName || null
                    });
                } else if (target.classList.contains('basic--button')) {
                    if (target.getAttribute('href') === '#brochure') {
                        this.events.viewBrochure(event, this.helpers, gradeMeta);
                    } else if (target.querySelector('.icomoon-spec-sheet')) {
                        this.events.viewSpecSheet(event, this.helpers, gradeMeta);
                    }
                }
            }
        });
    },

    /**
     * Configurator page tracking (Customise + Summary).
     */
    configuratorTracking() {
        if (!document.querySelector('.configurator-navigation')) return;

        // Sub-nav: finance options link
        $('body').on('click', '.configurator-navigation .region--addon-detail .summary-content', (event) => {
            const label = event.currentTarget.closest('.summary-item')?.querySelector('.summary-label')?.textContent?.trim() || null;

            this.configuratorEvents.subnavCta(this.helpers, {
                link_text: label,
                cta_type: null,
                model_id: this.helpers.configuratorProduct.model_id
            });
        });

        // Flyout menu items. In-page CTAs that proxy via .click() set
        // data-analytics-location (e.g. View brochure → in-page).
        $('body').on('click', '.configurator-modal .list-group-item-action', (event) => {
            const linkText = event.currentTarget.querySelector('.label')?.textContent?.trim()
                || event.currentTarget.textContent.trim()
                || null;
            const locationOnPage = event.currentTarget.dataset.analyticsLocation || 'three-dots';

            this.configuratorEvents.flyoutMenuItem(this.helpers, linkText, locationOnPage);
        });

        // Edit location in sticky footer
        $('body').on('click', '#configurator-footer-actions-select-location', () => {
            this.configuratorEvents.editLocation(this.helpers);
        });

        // Finance modal "Apply now" CTA
        $('body').on('click', '.summary-apply-for-finance-cta', (event) => {
            this.configuratorEvents.financeCta(this.helpers, {
                isSummary: this.helpers.isSummaryStage,
                isInFlyout: !!event.currentTarget.closest('.configurator-modal')
            });
        });

        // Finance application form rendered
        $(document).on('shown.bs.modal', '#summary-product-finance-modal', () => {
            this.configuratorEvents.financeFormView(this.helpers);
        });

        // Finance application form successfully submitted
        $('body').on('finance:formSubmitSuccess', () => {
            this.configuratorEvents.financeFormSubmit(this.helpers);
        });

        // Primary navigation header links
        $('body').on('click', '.menu-group .navbar-nav .nav-link:not(.search-btn)', (event) => {
            const target = event.currentTarget;
            const $topNavItem = $(target).closest('.navbar-nav > .nav-item');
            const $folderLink = $topNavItem.children('.nav-link').first();
            const isTopLevel = $folderLink[0] === target;

            this.configuratorEvents.primaryNavClick(
                this.helpers,
                snakeCase($folderLink.text().trim()),
                isTopLevel ? null : target.textContent.trim()
            );
        });
    },

    /**
     * Page Designer / footer CTAs → click_cta (v2 schema).
     */
    pageSectionCta() {
        $('body').on('click', 'a.basic--button, button.basic--button, .component.basic--button, a.subscribe-cta', (event) => {
            const target = event.currentTarget;

            if (target.closest('.component.common--navbar, .component.common--sticky-nav, .basic--decorator.style-02')) {
                return;
            }

            if (target.closest('.newsletter-form') || target.classList.contains('newsletter-form__submit')) {
                return;
            }

            if (target.classList.contains('action-offer-details')
                || target.classList.contains('action-build-and-price')
                || target.classList.contains('action-showroom')) {
                return;
            }

            this.events.pageSectionClick(event, this.helpers);
        });

        // Hero thumbnail strip (carousel stamped with data-location-onpage="Hero thumbnail").
        $('body').on('click', '[data-location-onpage="Hero thumbnail"] .swiper-slide', (event) => {
            this.events.heroThumbnailClick(event, this.helpers);
        });
    },

    /**
     * Product / promotion tile "View Showroom" → click_cta (not offers_*).
     */
    showroomTileClick() {
        $('body').on('click', SHOWROOM_TILE_SELECTOR, (event) => {
            this.events.showroomTileClick(event, this.helpers);
        });
    },

    offersCardClick() {
        $('body').on('click', OFFER_CARD_SELECTOR, (event) => {
            this.events.offersCardClick(event, this.helpers);
        });
    },

    offersSidesheetClick() {
        $('body').on('click', OFFER_SIDESHEET_SELECTOR, (event) => {
            this.events.offersSidesheetClick(event, this.helpers);
        });
    },

    newsletterForm() {
        $('body').on('focus input', '.newsletter-form__input', (event) => {
            this.events.newsletterFormStart(event, this.helpers);
        });

        document.addEventListener('amb:newsletter-success', (event) => {
            this.events.newsletterFormComplete(event, this.helpers);
        });
    },

    sirvSpin() {
        $('body').on('sirv:spin:ready sirv:spin:zoomIn sirv:spin:zoomOut sirv:spin:rotate sirv:spin:rotateEnd', event => {
            switch (event.type) {
                case 'sirv:spin:ready':
                    this.spinEvents.spinReady(event);
                    break;
                case 'sirv:spin:zoomIn':
                    this.spinEvents.spinZoomIn(event);
                    break;
                case 'sirv:spin:zoomOut':
                    this.spinEvents.spinZoomOut(event);
                    break;
                case 'sirv:spin:rotate':
                    this.spinEvents.spinRotate(event);
                    break;
                case 'sirv:spin:rotateEnd':
                    this.spinEvents.spinRotateEnd(event);
                    break;
            }
        });

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1 && node.classList.contains('smv-hotspot-pointer')) {
                            node.addEventListener('click', this.spinEvents.hotspotClick);
                        }
                    });

                    mutation.removedNodes.forEach((node) => {
                        if (node.nodeType === 1) {
                            if (node.classList.contains('smv-hotspot-pointer')) {
                                node.removeEventListener('click', this.spinEvents.hotspotClick);
                            } else if (node.classList.contains('Sirv')) {
                                this.spinEvents.spinExit(node);
                            }
                        }
                    });
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        window.addEventListener('pagehide', () => {
            document.querySelectorAll('.Sirv[data-src]').forEach((spinElement) => {
                if (spinElement.getAttribute('data-src').endsWith('.spin')) {
                    this.spinEvents.spinExit(spinElement);
                }
            });
        });
    },

    helpers: {
        get context() {
            if (this._pageContext === undefined) {
                const context = document.getElementById('analytics-context');

                this._pageContext = context ? JSON.parse(context.textContent) : {};
            }

            return this._pageContext;
        },

        get page() {
            return this.context.page || {};
        },

        get product() {
            return this.context.product || {};
        },

        get contentType() {
            const isShowroomPage = document.body.classList.contains('page--static') && this.product;

            return isShowroomPage ? 'showroom' : 'N/A';
        },

        get isSummaryStage() {
            return !!document.getElementById('configuratorSummary')?.classList.contains('active');
        },

        get configuratorBase() {
            return {
                context: this.isSummaryStage ? 'configurator_summary' : 'configurator_customise',
                site_session_id: document.querySelector('.page')?.dataset?.sessionId || null,
                timestamp_unix: Math.floor(Date.now() / 1000)
            };
        },

        get configuratorProduct() {
            const checkedGrade = document.querySelector('.grade-option-js:checked');
            const gradeLabel = checkedGrade?.closest('.radio-cards__card')?.querySelector('.variation-grade__display-value');
            const currentProduct = document.getElementById('current-product');

            return {
                model_id: currentProduct?.dataset?.selectedvariationid || currentProduct?.dataset?.pid || null,
                model: this.product.productName || null,
                grade: gradeLabel?.textContent?.trim() || null
            };
        },

        /**
         * BM Button Color label for cta_type (Primary, Secondary, Light, …).
         * Derived from btn- / btn-outline- / btn-link- classes. Special overrides
         * (thumbnail, subscribe-cta) pass through unchanged.
         *
         * @param {HTMLElement} el
         * @param {string} [override]
         * @returns {string}
         */
        getCtaType(el, override) {
            if (override) {
                const fromOverride = mapBtnClassToCtaType(override);

                return fromOverride || override;
            }

            if (!el || !el.classList) {
                return '';
            }

            if (el.classList.contains('subscribe-cta')) {
                return 'subscribe-cta';
            }

            const classes = Array.from(el.classList);
            const outline = classes.find((c) => /^btn-outline-[\w-]+$/.test(c));

            if (outline) {
                return mapBtnClassToCtaType(outline);
            }

            const linkVariant = classes.find((c) => /^btn-link-[\w-]+$/.test(c));

            if (linkVariant) {
                return mapBtnClassToCtaType(linkVariant);
            }

            const solid = classes.find((c) => /^btn-(primary|secondary|success|danger|warning|info|light|dark|transparent)$/.test(c));

            return solid ? mapBtnClassToCtaType(solid) : '';
        },

        /**
         * Always returns a string ('' when no navigable URL).
         *
         * @param {HTMLElement} el
         * @returns {string}
         */
        getLinkUrl(el) {
            if (!el) {
                return '';
            }

            if (el.tagName === 'A' && el.hasAttribute('href')) {
                const href = el.getAttribute('href');

                if (!href || href === '#' || href.indexOf('javascript:') === 0) {
                    return '';
                }

                return el.href || href;
            }

            return '';
        },

        /**
         * Collapsed text of a node, ignoring inline <style>/<script> that PD injects into the component root
         * (e.g. `.component--{id} { margin: … }` would otherwise leak into textContent).
         *
         * @param {HTMLElement} node
         * @returns {string}
         */
        getCleanText(node) {
            if (!node) {
                return '';
            }

            const clone = node.cloneNode(true);

            clone.querySelectorAll('style, script').forEach((child) => child.remove());

            return (clone.textContent || '').replace(/\s+/g, ' ').trim();
        },

        /**
         * Button / link label only.
         *
         * @param {HTMLElement} el
         * @returns {string}
         */
        getClickText(el) {
            if (!el) {
                return '';
            }

            const labeled = el.querySelector('.label, .btn-label');

            if (labeled) {
                return this.getCleanText(labeled);
            }

            const aria = el.getAttribute('aria-label');

            if (aria) {
                return aria.replace(/\s+/g, ' ').trim();
            }

            return this.getCleanText(el);
        },

        /**
         * @param {HTMLElement} el
         * @returns {{ card: HTMLElement|null, flyout: HTMLElement|null, activeView: HTMLElement|null }}
         */
        resolveOfferContext(el) {
            const cardSelector = OFFER_CARD_FAMILIES.join(', ');
            const flyout = el.closest(OFFER_FLYOUT_FAMILIES.join(', '));
            let card = el.closest(cardSelector);

            if (!card && flyout) {
                const trigger = document.querySelector('[data-target="#' + flyout.id + '"]');

                card = trigger ? trigger.closest(cardSelector) : null;
            }

            const activeView = flyout ? flyout.querySelector('.modal-view[view="' + flyout.dataset.view + '"]') : null;

            return { card: card, flyout: flyout, activeView: activeView };
        },

        /**
         * @param {HTMLElement} el
         * @returns {{ promo_id: string|null, campaign_id: string|null, model: string|null, grade: string|null }}
         */
        getOfferMeta(el) {
            const context = this.resolveOfferContext(el);
            const sources = [el];

            if (context.activeView) {
                sources.push(context.activeView);
            }

            if (context.card) {
                sources.push(context.card);
            }

            const read = (key) => {
                for (let i = 0; i < sources.length; i++) {
                    if (sources[i].dataset[key]) {
                        return sources[i].dataset[key];
                    }
                }

                return null;
            };

            return {
                promo_id: read('promoId'),
                campaign_id: read('campaignId'),
                model: read('model'),
                grade: read('grade')
            };
        },

        /**
         * Page content group from nearest [data-content-group]; empty string when unknown.
         *
         * @param {HTMLElement} [el]
         * @returns {string}
         */
        getContentGroup(el) {
            const anchor = el || document.body;
            const context = el ? this.resolveOfferContext(el) : { card: null };
            const wrapper = (context.card || anchor).closest
                ? (context.card || anchor).closest('[data-content-group]')
                : document.querySelector('.page[data-content-group]');
            const pageWrapper = document.querySelector('.page[data-content-group]');
            const node = wrapper || pageWrapper;
            const value = node && node.dataset ? node.dataset.contentGroup : '';

            return value || '';
        },

        /**
         * Resolve page_section / location.onpage / identity key for a click target.
         *
         * @param {HTMLElement} el
         * @returns {{ page_section: string, location_onpage: string, identity: string, cta_type_override: string }}
         */
        resolveSection(el) {
            if (el.closest('.footer-subscribe-container') || el.classList.contains('subscribe-cta')) {
                return {
                    page_section: 'Newsletter subscriber',
                    location_onpage: 'Footer newsletter subscriber',
                    identity: '',
                    cta_type_override: 'subscribe-cta'
                };
            }

            const idSection = this.resolveSectionById(el);

            if (idSection) {
                return idSection;
            }

            const headingSection = this.resolveSectionByHeading(el);
            const stamped = el.closest('[data-page-section]');

            // Prefer marketing section headings over the generic Split view stamp (Vehicle Tech embeds splits).
            if (headingSection && (!stamped || stamped.dataset.pageSection === 'Split view')) {
                return headingSection;
            }

            if (stamped) {
                return {
                    page_section: stamped.dataset.pageSection || '',
                    location_onpage: stamped.dataset.locationOnpage || stamped.dataset.pageSection || '',
                    identity: stamped.dataset.analyticsIdentity || '',
                    cta_type_override: stamped.dataset.ctaType || ''
                };
            }

            if (headingSection) {
                return headingSection;
            }

            return {
                page_section: '',
                location_onpage: '',
                identity: '',
                cta_type_override: ''
            };
        },

        /**
         * Resolve section from BM container IDs (#why-honda, #latest-news, #split-view).
         *
         * @param {HTMLElement} el
         * @returns {{ page_section: string, location_onpage: string, identity: string, cta_type_override: string }|null}
         */
        resolveSectionById(el) {
            for (let i = 0; i < SECTION_ID_RULES.length; i++) {
                const rule = SECTION_ID_RULES[i];

                if (el.closest(rule.selector)) {
                    return {
                        page_section: rule.page_section,
                        location_onpage: rule.location_onpage,
                        identity: rule.identity,
                        cta_type_override: ''
                    };
                }
            }

            return null;
        },

        /**
         * @param {HTMLElement} el
         * @returns {{ page_section: string, location_onpage: string, identity: string, cta_type_override: string }|null}
         */
        resolveSectionByHeading(el) {
            let node = el.parentElement;

            while (node && node !== document.body) {
                const heading = node.querySelector(':scope > .h1, :scope > .h2, :scope > .h3, :scope > h1, :scope > h2, :scope > h3, :scope > * > .h1, :scope > * > .h2, :scope > * > h1, :scope > * > h2');
                const candidates = heading
                    ? [heading]
                    : Array.from(node.querySelectorAll('h1, h2, h3, .h1, .h2, .h3')).slice(0, 3);

                for (let i = 0; i < candidates.length; i++) {
                    const text = this.getCleanText(candidates[i]);

                    for (let r = 0; r < HEADING_SECTION_RULES.length; r++) {
                        const rule = HEADING_SECTION_RULES[r];

                        if (rule.pattern.test(text)) {
                            return {
                                page_section: rule.page_section,
                                location_onpage: rule.location_onpage,
                                identity: rule.identity,
                                cta_type_override: ''
                            };
                        }
                    }
                }

                node = node.parentElement;
            }

            return null;
        },

        /**
         * Text of the last element matching `selector` that sits before `el` in document order.
         *
         * @param {HTMLElement} scope
         * @param {HTMLElement} el
         * @param {string} selector
         * @returns {string}
         */
        getPrecedingText(scope, el, selector) {
            const matches = scope.querySelectorAll(selector);

            for (let i = matches.length - 1; i >= 0; i--) {
                const match = matches[i];
                const precedes = Boolean(match.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING);
                const text = precedes && !match.contains(el) ? this.getCleanText(match) : '';

                if (text) {
                    return text;
                }
            }

            return '';
        },

        /**
         * Nearest title text for component_name / card_name / card_title.
         *
         * Walks up the Page Designer scopes around a CTA, so nested button stacks still find the
         * title of their card (Latest news keeps the title in `region--content` and the buttons in
         * a nested `region--items`). A heading always wins over the body copy that sits between
         * that heading and the CTA.
         *
         * @param {HTMLElement} el
         * @returns {string}
         */
        getNearestTitle(el) {
            let scope = el.closest(TITLE_SCOPE_SELECTOR) || el.parentElement;
            let bodyText = '';

            while (scope) {
                // A scope holding cards the CTA does not belong to is a section grid, so its text
                // names the section rather than this CTA — e.g. the "View all news" below the cards.
                const siblingCards = Array.from(scope.querySelectorAll(CARD_SELECTOR));

                if (siblingCards.some((card) => !card.contains(el))) {
                    break;
                }

                const title = this.getPrecedingText(scope, el, TITLE_SELECTOR);

                if (title) {
                    return title;
                }

                bodyText = bodyText || this.getPrecedingText(scope, el, BODY_TEXT_SELECTOR);

                // Stop at the CTA's own card or grid cell so it never inherits a neighbouring
                // card's title — e.g. "See active recalls" sits in its own cell and stays untitled.
                if (scope.matches(TITLE_BOUNDARY_SELECTOR)) {
                    break;
                }

                scope = scope.parentElement && scope.parentElement.closest(TITLE_SCOPE_SELECTOR);
            }

            return bodyText;
        },

        /**
         * @param {HTMLElement} el
         * @param {string} identity
         * @returns {object}
         */
        getIdentityFields(el, identity) {
            if (!identity) {
                return {};
            }

            const title = this.getNearestTitle(el);

            if (!title) {
                return {};
            }

            return { [identity]: title };
        },

        get modelId() {
            return this.product.masterID || this.product.id || null;
        },

        /**
         * Display model name for showroom context (not SKU).
         *
         * @returns {string|null}
         */
        get modelName() {
            return this.product.productName || this.product.model || null;
        },

        /**
         * Push a v2 click_cta payload.
         *
         * @param {HTMLElement} el
         * @param {object} fields
         */
        pushClickCta(el, fields) {
            const section = fields.page_section != null
                ? {
                    page_section: fields.page_section,
                    location_onpage: fields['location.onpage'] || fields.location_onpage || fields.page_section,
                    identity: fields.identity || '',
                    cta_type_override: fields.cta_type_override || ''
                }
                : this.resolveSection(el);

            const payload = {
                event: 'click_cta',
                content_group: fields.content_group != null ? fields.content_group : this.getContentGroup(el),
                page_section: section.page_section,
                'location.onpage': section.location_onpage,
                click_text: fields.click_text != null ? fields.click_text : this.getClickText(el),
                cta_type: fields.cta_type != null
                    ? fields.cta_type
                    : this.getCtaType(el, section.cta_type_override),
                link_url: fields.link_url != null ? fields.link_url : this.getLinkUrl(el),
                model: fields.model !== undefined ? fields.model : null,
                grade: fields.grade !== undefined ? fields.grade : null,
                ...this.getIdentityFields(el, fields.identity || section.identity),
                ...fields.extra
            };

            // Allow explicit identity overrides from caller.
            if (fields.component_name !== undefined) payload.component_name = fields.component_name;
            if (fields.card_name !== undefined) payload.card_name = fields.card_name;
            if (fields.card_title !== undefined) payload.card_title = fields.card_title;

            dataLayer.push(payload);
        }
    },

    events: {
        jumpLink(event, helpers) {
            const target = event.currentTarget;

            dataLayer.push({
                event: 'showroom_subnav_click',
                content_group: helpers.getContentGroup(target),
                page_section: 'Sub navigation',
                'location.onpage': 'Sub navigation',
                click_text: helpers.getClickText(target),
                link_url: helpers.getLinkUrl(target),
                cta_type: helpers.getCtaType(target)
            });
        },

        testDrive(event, helpers, initData = {}) {
            helpers.pushClickCta(event.currentTarget, {
                ...initData,
                model: initData.model !== undefined ? initData.model : helpers.modelName,
                grade: initData.grade !== undefined ? initData.grade : null
            });
        },

        buildAndPrice(event, helpers, initData = {}) {
            helpers.pushClickCta(event.currentTarget, {
                ...initData,
                model: initData.model !== undefined ? initData.model : helpers.modelName,
                grade: initData.grade !== undefined ? initData.grade : null
            });
        },

        seeAllFeatures(event, helpers, initData = {}) {
            helpers.pushClickCta(event.currentTarget, {
                ...initData,
                model: initData.model !== undefined ? initData.model : helpers.modelName,
                grade: initData.grade !== undefined ? initData.grade : null
            });
        },

        toggleNavbar(event, helpers) {
            helpers.pushClickCta(event.currentTarget, {
                page_section: 'Sub navigation',
                'location.onpage': 'Sub navigation',
                click_text: 'three_dots',
                cta_type: helpers.getCtaType(event.currentTarget) || 'navbar_toggler',
                link_url: '',
                model: helpers.modelName,
                grade: null
            });
        },

        toggleFilter(event, helpers, initData = {}) {
            dataLayer.push({
                event: 'filter_icon',
                content_group: helpers.getContentGroup(event.currentTarget),
                page_section: initData.page_section || 'Grades',
                'location.onpage': initData['location.onpage'] || 'Grades',
                model: initData.model || null,
                grade: null
            });
        },

        filterApply(event, helpers, initData = {}) {
            const target = event.currentTarget;
            const form = target.closest('.vehicle-grades-carousel__filter-sheet-content');
            const filtersSelectedObj = Array.from(form.querySelectorAll('.vehicle-grades-carousel__filter-sheet-section'))
                .reduce((values, section) => {
                    const sectionTitle = snakeCase(section.querySelector('.vehicle-grades-carousel__filter-sheet-section-title')?.textContent?.trim() || '');
                    const sectionOptions = section.querySelectorAll('.vehicle-grades-carousel__filter-sheet-option.active');

                    if (sectionTitle && sectionOptions.length > 0) {
                        values[sectionTitle] = Array.from(sectionOptions).map(option => option.textContent.trim());
                    }

                    return values;
                }, {});

            const filtersSelected = Object.entries(filtersSelectedObj)
                .map(([group, vals]) => `${group}: ${vals.map(v => snakeCase(v).replace(/_/g, ' ')).join(', ')}`)
                .join(', ');

            dataLayer.push({
                event: 'filter_apply',
                content_group: helpers.getContentGroup(target),
                page_section: initData.page_section || 'Grades',
                'location.onpage': initData['location.onpage'] || 'Grades',
                filter_group: null,
                filter_value: null,
                filters_selected: filtersSelected,
                model: initData.model || null,
                grade: null
            });
        },

        filterValueSelect(event, helpers, initData = {}) {
            const target = event.currentTarget;

            setTimeout(() => {
                const form = target.closest('.vehicle-grades-carousel__filter-pills');
                const activePills = form
                    ? Array.from(form.querySelectorAll('.vehicle-grades-carousel__filter-pill.active'))
                    : [];

                const filtersSelectedObj = activePills.reduce((values, filter) => {
                    const sectionTitle = snakeCase(filter.dataset.filterGroup);

                    if (!values[sectionTitle]) {
                        values[sectionTitle] = [];
                    }

                    values[sectionTitle].push(filter.dataset.filterValue);

                    return values;
                }, {});

                const filtersSelected = Object.entries(filtersSelectedObj)
                    .map(([group, vals]) => `${group}: ${vals.map(v => snakeCase(v).replace(/_/g, ' ')).join(', ')}`)
                    .join(', ');

                dataLayer.push({
                    event: 'filter_value_select',
                    content_group: helpers.getContentGroup(target),
                    page_section: initData.page_section || 'Grades',
                    'location.onpage': initData['location.onpage'] || 'Grades',
                    filter_group: snakeCase(target.dataset.filterGroup),
                    filter_value: snakeCase(target.dataset.filterValue).replace(/_/g, ' '),
                    filters_selected: filtersSelected,
                    model: initData.model || null,
                    grade: null
                });
            }, 0);
        },

        viewBrochure(event, helpers, initData = {}) {
            helpers.pushClickCta(event.currentTarget, {
                ...initData,
                model: initData.model !== undefined ? initData.model : helpers.modelName,
                grade: initData.grade !== undefined ? initData.grade : null
            });
        },

        viewSpecSheet(event, helpers, initData = {}) {
            helpers.pushClickCta(event.currentTarget, {
                ...initData,
                model: initData.model !== undefined ? initData.model : helpers.modelName,
                grade: initData.grade !== undefined ? initData.grade : null
            });
        },

        stickyNavCta(event, helpers, initData = {}) {
            helpers.pushClickCta(event.currentTarget, {
                page_section: 'Sub navigation',
                'location.onpage': 'Sub navigation',
                model: initData.model !== undefined ? initData.model : helpers.modelName,
                grade: initData.grade !== undefined ? initData.grade : null
            });
        },

        /**
         * Generic PD / footer CTA.
         */
        pageSectionClick(event, helpers) {
            helpers.pushClickCta(event.currentTarget, {});
        },

        /**
         * Hero thumbnail slide click.
         */
        heroThumbnailClick(event, helpers) {
            const target = event.currentTarget;
            const img = target.querySelector('img');
            const label = (img && (img.getAttribute('alt') || img.getAttribute('title'))) || 'thumbnail';

            helpers.pushClickCta(target, {
                page_section: 'Hero',
                'location.onpage': 'Hero thumbnail',
                click_text: label,
                cta_type: 'thumbnail',
                link_url: '',
                component_name: label,
                identity: ''
            });
        },

        /**
         * Tile View Showroom action → click_cta.
         */
        showroomTileClick(event, helpers) {
            const target = event.currentTarget;
            const { card } = helpers.resolveOfferContext(target);
            const titleEl = card && (card.querySelector('.card-title-text') || card.querySelector('.card-title'));
            const meta = helpers.getOfferMeta(target);
            const section = helpers.resolveSection(target);
            const contentGroup = helpers.getContentGroup(target);
            const fallbackLineup = contentGroup === 'Homepage';

            helpers.pushClickCta(target, {
                page_section: section.page_section || (fallbackLineup ? 'Meet the line-up' : ''),
                'location.onpage': section.location_onpage || (fallbackLineup ? 'Lineup' : ''),
                card_name: titleEl?.textContent.trim() || '',
                identity: '',
                model: meta.model,
                grade: meta.grade
            });
        },

        offersCardClick(event, helpers) {
            const target = event.currentTarget;
            const { card } = helpers.resolveOfferContext(target);
            const titleEl = card && (card.querySelector('.card-title-text') || card.querySelector('.card-title'));

            dataLayer.push({
                event: 'offers_card_click',
                content_group: helpers.getContentGroup(target),
                page_section: 'Offers card',
                'location.onpage': 'Offers card',
                title: titleEl?.textContent.trim() || null,
                click_text: helpers.getClickText(target),
                link_url: helpers.getLinkUrl(target),
                ...helpers.getOfferMeta(target)
            });
        },

        offersSidesheetClick(event, helpers) {
            const target = event.currentTarget;
            const { flyout } = helpers.resolveOfferContext(target);
            const clickedView = target.closest('.modal-view');
            const viewName = clickedView ? clickedView.getAttribute('view') : flyout?.dataset.view;
            const isListings = viewName === 'promotion-list';

            let clickText = helpers.getClickText(target);

            if (target.classList.contains('accordion-title')) {
                const labelNode = Array.from(target.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());

                clickText = (labelNode ? labelNode.textContent : helpers.getClickText(target)).replace(/\s+/g, ' ').trim();
            }

            if (target.classList.contains('close')) {
                clickText = 'Close [X]';
            }

            const activeView = flyout?.querySelector('.modal-view[view="' + flyout.dataset.view + '"]');
            const title = target.closest('.promotion-card')?.querySelector('.title')?.textContent.trim()
                || clickedView?.querySelector('.title')?.textContent.trim()
                || activeView?.querySelector('.title')?.textContent.trim()
                || null;

            dataLayer.push({
                event: 'offers_sidesheet_click',
                content_group: helpers.getContentGroup(target),
                page_section: 'Offers sidesheet',
                page_depth: isListings ? 'sidesheet listings' : 'sidesheet details',
                'location.onpage': isListings ? 'Offers sidesheet - listings' : 'Offers sidesheet - details',
                title: title,
                click_text: clickText,
                link_url: helpers.getLinkUrl(target),
                ...helpers.getOfferMeta(target)
            });
        },

        newsletterFormStart(event, helpers) {
            const target = event.currentTarget;
            const section = helpers.resolveSection(target);
            const locationKey = section.location_onpage || 'In-page newsletter subscriber';

            if (!analyticsHelper.isFirstFormInteraction('newsletter_subscribe_' + locationKey)) {
                return;
            }

            dataLayer.push({
                event: 'newsletter_form_start',
                content_group: helpers.getContentGroup(target),
                page_section: section.page_section || 'Newsletter subscriber',
                'location.onpage': locationKey,
                form_name: 'newsletter_subscribe',
                form_field: 'email_address',
                form_status: 'start'
            });
        },

        newsletterFormComplete(event, helpers) {
            const detail = event.detail || {};
            const form = detail.form || document.querySelector('.newsletter-form');
            const anchor = form || document.body;
            const section = helpers.resolveSection(anchor);

            dataLayer.push({
                event: 'newsletter_form_complete',
                content_group: helpers.getContentGroup(anchor),
                page_section: section.page_section || 'Newsletter subscriber',
                'location.onpage': section.location_onpage || 'In-page newsletter subscriber',
                form_name: 'newsletter_subscribe',
                form_status: 'success'
            });
        }
    },

    spinEvents: {
        spinInteractionStartTime: 0,
        spinInteractionTime: 0,
        spinInteractionsCount: 0,

        spinInteractionTimeQualified: false,
        spinInteractionsCountQualified: false,
        spinEngagementQualified: false,

        getContext() {
            if (this._ctx === undefined) {
                const el = document.getElementById('analytics-context');

                this._ctx = el ? JSON.parse(el.textContent) : {};
            }

            return this._ctx;
        },

        spinReady(event) {
            const spinId = event.originalEvent.detail.id;
            const ctx = this.getContext();

            dataLayer.push({
                event: 'spin_load',
                model_id: ctx.product?.masterID || ctx.product?.id || null,
                site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                spin: {
                    spin_id: spinId
                }
            });
        },

        spinInteraction(event) {
            const { type, timeStamp } = event;

            this.spinInteractionStartTime = parseInt(timeStamp, 10);
            this.spinInteractionsCount++;

            if (this.spinInteractionsCount === 1) {
                this.firstInteraction(event);
            } else {
                this.subsequentInteraction(event);
            }
        },

        firstInteraction(event) {
            const { type, originalEvent: { detail: { id: spinId } } } = event;
            const ctx = this.getContext();

            dataLayer.push({
                event: 'spin_interaction_start',
                model_id: ctx.product?.masterID || ctx.product?.id || null,
                site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                spin: {
                    spin_id: spinId,
                    input_type: type === 'sirv:spin.zoomIn' ? 'zoomIn'
                        : type === 'sirv:spin.zoomOut' ? 'zoomOut'
                            : type === 'sirv:spin.rotate' ? 'rotate'
                                : 'drag'
                }
            });
        },

        subsequentInteraction(event) {
            const { originalEvent: { detail: { id: spinId } } } = event;
            const ctx = this.getContext();

            dataLayer.push({
                event: 'spin_interactions_2',
                model_id: ctx.product?.masterID || ctx.product?.id || null,
                site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                spin: {
                    spin_id: spinId,
                    interactions_count: this.spinInteractionsCount
                }
            });
        },

        spinZoomIn(event) {
            this.spinInteraction(event);
        },

        spinZoomOut(event) {
            this.spinInteraction(event);
        },

        spinRotate(event) {
            this.spinInteraction(event);

            const spinId = event.originalEvent.detail.id;
            const ctx = this.getContext();

            dataLayer.push({
                event: 'spin_rotate',
                model_id: ctx.product?.masterID || ctx.product?.id || null,
                site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                spin: {
                    spin_id: spinId
                }
            });
        },

        spinRotateEnd(event) {
            const spinId = event.originalEvent.detail.id;
            const ctx = this.getContext();

            this.spinInteractionTime += parseInt(event.timeStamp, 10) - this.spinInteractionStartTime;

            if (!this.spinInteractionTimeQualified && this.spinInteractionTime >= 3000) {
                this.spinInteractionTimeQualified = true;

                dataLayer.push({
                    event: 'spin_time_3s',
                    model_id: ctx.product?.masterID || ctx.product?.id || null,
                    site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                    spin: {
                        spin_id: spinId
                    }
                });
            }

            if (!this.spinEngagementQualified && (this.spinInteractionTime >= 3000 || this.spinInteractionsCount >= 2)) {
                this.spinEngagementQualified = true;
                dataLayer.push({
                    event: 'spin_engagement_qualified',
                    model_id: ctx.product?.masterID || ctx.product?.id || null,
                    site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                    spin: {
                        spin_id: spinId,
                        qualified_by: this.spinInteractionTime >= 3000 ? 'time' : 'interactions',
                        interaction_time_ms: this.spinInteractionTime,
                        interactions_count: this.spinInteractionsCount
                    }
                });
            }
        },

        spinExit(spinElement) {
            const spinId = spinElement?.getAttribute('id') || 'N/A';
            const ctx = this.getContext();

            dataLayer.push({
                event: 'spin_exit',
                model_id: ctx.product?.masterID || ctx.product?.id || null,
                site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                spin: {
                    spin_id: spinId,
                    interaction_time_ms: this.spinInteractionTime,
                    interactions_count: this.spinInteractionsCount
                }
            });
        },

        hotspotClick(event) {
            const sirv = event.target.closest('.Sirv');
            const spinId = sirv?.getAttribute('id') || 'N/A';
            const analyticsEl = document.getElementById('analytics-context');
            const ctx = analyticsEl ? JSON.parse(analyticsEl.textContent) : {};

            dataLayer.push({
                event: 'spin_hotspot_click',
                model_id: ctx.product?.masterID || ctx.product?.id || null,
                site_session_id: ctx.sessionId || document.querySelector('.page')?.dataset?.sessionId || null,
                spin: {
                    spin_id: spinId
                }
            });
        }
    },

    /**
     * Car Explorer directive tracking events.
     * Called from car-explorer.directive.js rather than auto-initialised.
     */
    carExplorerTracking: {
        trackNavArrow(state) {
            try {
                const ctx = getCarExplorerContext(state);
                const url = getCarExplorerUrlParams();

                dataLayer.push({
                    'event': 'carousel_nav_arrow',
                    'action': 'click',
                    'content_type': ctx.contentType,
                    'context': ctx.view,
                    'location.onpage': 'car_explorer',

                    'link_text': null,
                    'link_url': window.location.href,

                    'suburb': url.suburb,
                    'postcode': url.postcode,
                    'state': url.state,
                    'campaign_ids': url.campaignIds,
                    'model_id': ctx.modelId,
                    'model': ctx.model,
                    'grade': ctx.grade || null,
                    'sub_variant': url.subVariant,

                    'site_session_id': ctx.sessionId,
                    'timestamp_unix': ctx.timestamp,

                    'outbound': false
                });
            } catch (err) {
                console.error('GA tracking error:', err);
            }
        },

        trackZoneSwitch(state, zone) {
            try {
                const ctx = getCarExplorerContext(state);

                dataLayer.push({
                    'event': 'zone_switch',
                    'action': 'click',
                    'content_type': ctx.contentType,
                    'context': zone,
                    'location.onpage': 'car_explorer',

                    'model_id': ctx.modelId,
                    'model': ctx.model,
                    'grade': ctx.grade,

                    'site_session_id': ctx.sessionId,
                    'timestamp_unix': ctx.timestamp
                });
            } catch (err) {
                console.error('GA tracking error:', err);
            }
        },

        trackNightMode(state) {
            try {
                const ctx = getCarExplorerContext(state);

                dataLayer.push({
                    'event': 'night_mode',
                    'action': 'click',
                    'content_type': '360_spinner',
                    'context': 'toggle_night_mode',
                    'link_text': ctx.isConfigurator ? 'Images carousel' : 'Design',
                    'location.onpage': ctx.isConfigurator ? 'Configurator' : 'Showroom',

                    'model_id': ctx.modelId,
                    'model': ctx.model,
                    'grade': ctx.grade,

                    'site_session_id': ctx.sessionId,
                    'timestamp_unix': ctx.timestamp
                });
            } catch (err) {
                console.error('GA tracking error:', err);
            }
        }
    },

    /**
     * Configurator page tracking events (Customise + Summary).
     * Called from callToActions() and configuratorTracking().
     */
    configuratorEvents: {
        subnavClick(helpers, linkText) {
            dataLayer.push({
                event: 'configurator_subnav_click',
                content_type: 'configurator',
                'location.onpage': 'sub_navigation',
                link_text: linkText,
                ...helpers.configuratorBase
            });
        },

        subnavCta(helpers, data) {
            dataLayer.push({
                event: 'click_cta',
                action: 'click',
                content_type: 'configurator',
                'location.onpage': 'sub_navigation',
                ui_experience: null,
                ...data,
                ...helpers.configuratorBase
            });
        },

        flyoutMenuItem(helpers, linkText, locationOnPage) {
            dataLayer.push({
                event: 'click_cta',
                action: 'click',
                content_type: 'configurator',
                'location.onpage': locationOnPage || 'three-dots',
                link_text: linkText,
                model_id: helpers.configuratorProduct.model_id,
                ui_experience: null,
                ...helpers.configuratorBase
            });
        },

        editLocation(helpers) {
            dataLayer.push({
                event: 'click_cta',
                action: 'click',
                content_type: 'edit_location',
                'location.onpage': 'sticky_footer',
                model_id: helpers.configuratorProduct.model_id,
                ui_experience: null,
                ...helpers.configuratorBase
            });
        },

        financeCta(helpers, { isSummary, isInFlyout }) {
            dataLayer.push({
                event: 'click_cta',
                action: 'click',
                content_type: isSummary ? 'finance_modal' : 'finance_CTA',
                'location.onpage': isInFlyout ? 'three-dots' : (isSummary ? 'in-page' : 'finance_modal'),
                link_text: 'apply_now',
                ...helpers.configuratorProduct,
                ...helpers.configuratorBase
            });
        },

        financeFormView(helpers) {
            const openFromPage = helpers.isSummaryStage ? 'summary' : 'customise';

            dataLayer.push({
                event: 'form_view',
                content_type: 'finance',
                'location.onpage': 'finance_form',
                form_name: 'apply_for_finance',
                open_from_page: openFromPage,
                link_text: null,
                ...helpers.configuratorProduct,
                ...helpers.configuratorBase
            });
        },

        primaryNavClick(helpers, navFolder, linkText) {
            dataLayer.push({
                event: 'primarynav_click',
                action: 'click',
                content_type: 'navigation',
                'location.onpage': 'sub_navigation',
                nav_folder: navFolder,
                link_text: linkText,
                ...helpers.configuratorBase
            });
        },

        financeFormSubmit(helpers) {
            dataLayer.push({
                event: 'form_submit',
                form_name: 'apply_for_finance',
                action: 'complete',
                content_type: 'finance',
                'location.onpage': 'finance_form',
                optIn: !!(document.getElementById('flyout-finance-marketing-consent') ||
                    document.getElementById('privacyConsentFinance'))?.checked,
                ...helpers.configuratorProduct,
                ...helpers.configuratorBase
            });
        }
    }
};
